<template>
  <div
    class="fts-settings"
    :class="{ 'is-phone': phone, 'is-phone-home': phoneHome, 'is-phone-page': phonePage }"
  >
    <!-- sidebar. On a phone it is the first screen (D-11, board 10), and a page replaces it. -->
    <nav class="fts-settings-nav" aria-label="Settings" v-show="!phonePage">
      <div class="fts-settings-nav-top">
        <a href="#" class="fts-settings-back" aria-label="Back to workspace" @click.prevent="backToWorkspace">
          <font-awesome-icon :icon="['fas', 'angle-left']" />
          <span>Back to workspace</span>
        </a>
        <span class="fts-settings-bar-title" v-if="phone">Settings</span>
        <fts-release-badge v-if="stageBadge" v-bind="stageBadge" />
      </div>

      <label class="fts-settings-find">
        <font-awesome-icon :icon="['fas', 'search']" />
        <input
          type="search"
          v-model="filter"
          aria-label="Find a setting"
          placeholder="Find a setting"
        />
      </label>

      <!-- Two halves, each headed by whom it is about: you, the same in every workspace; and
           this workspace. The avatar opens the first, the workspace menu the second. -->
      <div
        class="fts-settings-half"
        :class="'is-' + half.id"
        v-for="half in halves"
        :key="half.id"
      >
        <div class="fts-settings-ws" v-if="half.id === 'workspace'">
          <span class="fts-settings-ws-mark">{{ workspaceInitials }}</span>
          <span class="fts-settings-ws-text">
            <span class="fts-settings-ws-name">{{ workspaceName }}</span>
          </span>
        </div>
        <div class="fts-settings-ws is-person" v-else>
          <fts-profile
            class="fts-settings-person-mark"
            :profile="person"
            :profile-photo-size="36"
            :profile-name="false"
          />
          <span class="fts-settings-ws-text">
            <span class="fts-settings-ws-name">{{ personName }}</span>
            <span class="fts-settings-ws-sub">The same in every workspace</span>
          </span>
        </div>

        <!-- The phone home leads with where the workspace stands. That is Overview, so it is
             shown here instead of listed (and comes back to the list while searching). -->
        <settings-overview
          v-if="half.id === 'workspace' && phoneHome && !filter"
          compact
          :workspace-id="workspaceId"
        />

        <div class="fts-settings-group" v-for="group in half.groups" :key="group.id">
          <!-- The person heading the half already names the account group. -->
          <div class="fts-settings-group-name" v-if="half.id === 'workspace'">{{ group.name }}</div>
          <a
            v-for="item in group.items"
            :key="item.id"
            href="#"
            class="fts-settings-item"
            :class="{ 'is-active': item.moved && item.id === section, 'is-elsewhere': !item.moved }"
            :aria-current="item.moved && item.id === section ? 'page' : null"
            @click.prevent="go(item)"
          >
            <font-awesome-icon :icon="['fas', item.icon]" class="fts-settings-item-icon" />
            <span class="fts-settings-item-name">{{ item.name }}</span>
            <span class="fts-settings-item-note" v-if="!item.moved">{{ item.comingSoon ? 'Soon' : '' }}</span>
            <font-awesome-icon
              v-if="!item.moved && !item.comingSoon"
              :icon="['fas', 'up-right-from-square']"
              class="fts-settings-item-out"
            />
            <font-awesome-icon
              v-if="phone && item.moved"
              :icon="['fas', 'chevron-right']"
              class="fts-settings-item-chev"
            />
          </a>
        </div>

        <!-- Log out lived only in the old account dialog, which the avatar no longer opens. -->
        <template v-if="half.id === 'you' && !filter">
          <a href="#" class="fts-settings-item is-signout" @click.prevent="logout">
            <font-awesome-icon :icon="['fas', 'sign-out-alt']" class="fts-settings-item-icon" />
            <span class="fts-settings-signout-name">Log out</span>
          </a>
          <div class="fts-settings-version" v-if="buildVersion">Version {{ buildVersion }}</div>
        </template>
      </div>

      <div class="fts-settings-nav-empty" v-if="!navGroups.length">
        Nothing matches &ldquo;{{ filter }}&rdquo;.
      </div>
    </nav>

    <!-- content -->
    <main class="fts-settings-main fts-scrollbar fts-scrollbar-overlay" v-if="!phoneHome">
      <a href="#" class="fts-settings-phone-back" v-if="phone" @click.prevent="goHome">
        <font-awesome-icon :icon="['fas', 'angle-left']" />
        <span>Settings</span>
      </a>
      <!-- Said once, by the frame, for every account page: the workspace named at the top of the
           sidebar has no say over these, and a change here follows the person everywhere. -->
      <div class="fts-settings-scope" v-if="accountScope">
        <font-awesome-icon :icon="['fas', 'user']" />
        <span>Your account &middot; the same in every workspace you belong to</span>
      </div>
      <component
        :is="sectionComponent"
        v-if="sectionComponent"
        :workspace-id="workspaceId"
        @deleted="onWorkspaceGone"
        @left="onWorkspaceGone"
      />
    </main>
  </div>
</template>

<script>
import { clientStore } from "../store/clientStore";
import { authorizationStore } from "../store/authorizationStore";
import webAppConfig from "../commons/webApp.config";
import commons from "../commons/commons";
import { builderNotAvailableMessage } from "../commons/message";
import FTSProfile from "../elements/FTSProfile";
import { SETTINGS_GROUPS, settingsItem, movedSections, isAccountSection } from "./settingsNav";
import { settingsStageBadge, settingsSectionAllowed } from "./settingsStage";
import { settingsRoleAllowed } from "./settingsAccess";
import SettingsOverview from "./sections/SettingsOverview";
import SettingsPlan from "./sections/SettingsPlan";
import SettingsSecurity from "./sections/SettingsSecurity";
import SettingsGney from "./sections/SettingsGney";
import SettingsCredits from "./sections/SettingsCredits";
import SettingsBilling from "./sections/SettingsBilling";
import SettingsUsage from "./sections/SettingsUsage";
import SettingsGeneral from "./sections/SettingsGeneral";
import SettingsPeople from "./sections/SettingsPeople";
import SettingsProfile from "./sections/SettingsProfile";
import SettingsPassword from "./sections/SettingsPassword";
import SettingsAppearance from "./sections/SettingsAppearance";
import SettingsConnections from "./sections/SettingsConnections";
import SettingsAutoCount from "./sections/SettingsAutoCount";
import SettingsDataSources from "./sections/SettingsDataSources";
import SettingsPortals from "./sections/SettingsPortals";

// Below this the sidebar stops being a column and becomes the first screen (D-11, board 10).
const PHONE_QUERY = "(max-width: 767px)";

const SECTION_COMPONENTS = {
  overview: SettingsOverview,
  gney: SettingsGney,
  plan: SettingsPlan,
  credits: SettingsCredits,
  billing: SettingsBilling,
  usage: SettingsUsage,
  general: SettingsGeneral,
  people: SettingsPeople,
  profile: SettingsProfile,
  password: SettingsPassword,
  appearance: SettingsAppearance,
  connections: SettingsConnections,
  security: SettingsSecurity,
  autocount: SettingsAutoCount,
  "data-sources": SettingsDataSources,
  portals: SettingsPortals,
};

export default {
  name: "FTSSettings",
  components: {
    "fts-profile": FTSProfile,
    "settings-overview": SettingsOverview,
    "settings-plan": SettingsPlan,
    "settings-security": SettingsSecurity,
    "settings-gney": SettingsGney,
    "settings-credits": SettingsCredits,
    "settings-billing": SettingsBilling,
    "settings-usage": SettingsUsage,
    "settings-general": SettingsGeneral,
    "settings-people": SettingsPeople,
    "settings-profile": SettingsProfile,
    "settings-password": SettingsPassword,
    "settings-appearance": SettingsAppearance,
    "settings-connections": SettingsConnections,
    "settings-autocount": SettingsAutoCount,
    "settings-data-sources": SettingsDataSources,
    "settings-portals": SettingsPortals,
  },
  data() {
    return {
      filter: "",
      phone: false,
      media: null,
    };
  },
  computed: {
    /**
     * On a phone the frame is two screens rather than two columns: the home (the
     * sidebar, led by Overview's status) and a page on its own with a way back.
     * Overview IS the home there, so asking for it lands on the list.
     */
    phoneHome() {
      return this.phone && this.section === "overview";
    },
    phonePage() {
      return this.phone && this.section !== "overview";
    },
    /** The sidebar's groups, less Overview on the phone home, where it is already on top. */
    navGroups() {
      if (!this.phoneHome || this.filter) return this.visibleGroups;
      return this.visibleGroups
        .map(group => ({ ...group, items: group.items.filter(item => item.id !== "overview") }))
        .filter(group => group.items.length);
    },
    /**
     * The sidebar in two halves: you (the "Your account" group) and this workspace (the rest).
     * You come first, as the avatar that opens them is yours. On the phone home the workspace
     * leads instead: there the home IS its Overview (board 10). A half with nothing left after a
     * search is dropped; the workspace half stays on the phone home for its status.
     */
    halves() {
      const you = { id: "you", groups: this.navGroups.filter(group => group.scope === "account") };
      const workspace = { id: "workspace", groups: this.navGroups.filter(group => group.scope !== "account") };
      const order = this.phoneHome ? [workspace, you] : [you, workspace];
      return order.filter(half => half.groups.length || (half.id === "workspace" && this.phoneHome && !this.filter));
    },
    person() {
      return authorizationStore.getters.userProfile || {};
    },
    personName() {
      const p = this.person;
      return p.displayName || p.fullName || p.userName || "You";
    },
    buildVersion() {
      return webAppConfig.buildVersion;
    },
    /**
     * Sections with a page here that this person is allowed to open. All three
     * matter: `moved` is whether a page exists, `stage` is whether it is theirs
     * to see yet, `access` is whether their role in this workspace reaches it
     * (D-12) -- and a URL typed by hand must respect the last two as much as the
     * sidebar does.
     */
    availableSections() {
      return movedSections().filter(id => this.reachable(settingsItem(id)));
    },
    section() {
      const asked = this.$route.params.section;
      return this.availableSections.indexOf(asked) >= 0 ? asked : "overview";
    },
    /** The page open now is the person's own, not the workspace's. */
    accountScope() {
      return isAccountSection(this.section);
    },
    sectionComponent() {
      return SECTION_COMPONENTS[this.section] || null;
    },
    stageBadge() {
      return settingsStageBadge();
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceName() {
      return clientStore.getters.workspaceName || "Workspace";
    },
    workspaceInitials() {
      const words = String(this.workspaceName).trim().split(/\s+/).filter(Boolean);
      if (!words.length) return "?";
      return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0]).toUpperCase();
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    visibleGroups() {
      const needle = this.filter.trim().toLowerCase();
      return SETTINGS_GROUPS.map(group => {
        const items = group.items.filter(item => {
          if (!this.reachable(item)) return false;
          if (!needle) return true;
          return item.name.toLowerCase().indexOf(needle) >= 0 || group.name.toLowerCase().indexOf(needle) >= 0;
        });
        return { ...group, items };
      }).filter(group => group.items.length);
    },
  },
  methods: {
    /** One item's whole gate: staff-only rows, the release stage, and the role here. */
    reachable(item) {
      if (!item) return false;
      if (item.internalOnly && !this.isInternal) return false;
      return settingsSectionAllowed(item) && settingsRoleAllowed(item);
    },
    backToWorkspace() {
      this.$router.push({ name: "dashboard" }).catch(() => {});
    },
    /** As the old account dialog did, builder sessions included. */
    logout() {
      if (commons.builderMode) {
        this.$toasted.info(builderNotAvailableMessage("Log Out"));
        return;
      }
      authorizationStore.dispatch("logout");
    },
    /**
     * This workspace was deleted, or the person just left it: the shell moves them to another
     * one, exactly as after a delete from the old dialog. Nothing here is theirs to show now.
     */
    onWorkspaceGone(id) {
      window.eventBus.$emit("settings:workspaceGone", { id: id || this.workspaceId });
    },
    /** From a page back to the phone home. */
    goHome() {
      this.$router.push({ name: "settings", params: { section: "overview" } }).catch(() => {});
    },
    onMedia(event) {
      this.phone = event.matches;
    },
    go(item) {
      if (item.moved) {
        if (item.id === this.section) return;
        this.$router.push({ name: "settings", params: { section: item.id } }).catch(() => {});
        return;
      }
      if (item.comingSoon) return;
      // Not moved yet: take them to where it still lives, rather than to a dead
      // page. Nothing is retired before GA (W-25).
      const legacy = item.legacy || {};
      if (legacy.route) {
        this.$router.push({ name: legacy.route }).catch(() => {});
      } else if (legacy.modal) {
        window.eventBus.$emit("settings:openLegacy", { section: legacy.modal });
      }
    },
  },
  watch: {
    "$route.params.section"(value) {
      // An unknown or not-yet-moved section in the URL settles on Overview
      // rather than rendering nothing.
      if (value && this.availableSections.indexOf(value) < 0) {
        this.$router.replace({ name: "settings", params: { section: "overview" } }).catch(() => {});
      }
    },
  },
  created() {
    // Decided before the first render, so a phone never flashes the two-column frame.
    if (window.matchMedia) {
      this.media = window.matchMedia(PHONE_QUERY);
      this.phone = this.media.matches;
      if (this.media.addEventListener) this.media.addEventListener("change", this.onMedia);
      else if (this.media.addListener) this.media.addListener(this.onMedia);
    }

    const asked = this.$route.params.section;
    if (asked && this.availableSections.indexOf(asked) < 0) {
      const item = settingsItem(asked);
      // A section that has not moved yet and is theirs to open: send them where it
      // still lives. One their role or stage does not reach settles on Overview,
      // exactly like one that does not exist.
      if (item && !item.moved && this.reachable(item)) {
        this.$nextTick(() => this.go(item));
      }
      this.$router.replace({ name: "settings", params: { section: "overview" } }).catch(() => {});
    }
  },
  beforeDestroy() {
    if (!this.media) return;
    if (this.media.removeEventListener) this.media.removeEventListener("change", this.onMedia);
    else if (this.media.removeListener) this.media.removeListener(this.onMedia);
  },
};
</script>

<style lang="scss">
/* Workspace settings (alpha) — docs/frontend-workspace-settings-proposal.md §3 D-11.
   Scoped to .fts-settings so the ruled 8px/12px radii and primary-700 actions do
   not leak into the rest of the app before W-24 is applied more widely. */
.fts-settings {
  --fts-settings-ink: var(--inistate-color-neutral-900);
  --fts-settings-ink-2: var(--inistate-color-neutral-800);
  --fts-settings-mute: var(--inistate-color-neutral-700);
  --fts-settings-faint: var(--inistate-color-neutral-600);
  --fts-settings-line: var(--inistate-color-neutral-200);
  --fts-settings-hair: var(--inistate-color-neutral-100);
  --fts-settings-ground: var(--inistate-color-neutral-50);
  --fts-settings-accent: var(--inistate-color-primary-700);
  --fts-settings-accent-soft: var(--inistate-color-primary-50);
  --fts-settings-accent-line: var(--inistate-color-primary-100);
  /* text and icons drawn ON the accent (a primary button, the workspace mark) */
  --fts-settings-on-accent: #ffffff;

  display: flex;
  height: 100%;
  min-height: 0;
  background: var(--inistate-color-shade-white);
  color: var(--fts-settings-ink);
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-variant-numeric: tabular-nums;

  a {
    color: var(--fts-settings-accent);
  }
}

/* SS06280: dark theme. Settings is drawn from the fixed token palette (--inistate-color-*), which the
   theme switch never touches, while the form fields inside it follow the theme: dark fields on a
   white page. Under body.dark the tokens Settings uses are given dark values HERE, for this area
   only, so every section follows without a rule of its own:
   - neutrals map onto the app's dark scale (--fts-gray-*), which runs the other way round: surfaces
     dark, text light;
   - a 50 / 100 tint (a soft background and its border) becomes a translucent wash of its own hue;
   - the strong shades used for text and status dots (primary-800, green-800/900, orange-900,
     red-600) become light shades of the same hue;
   - the accent is lifted so links read on a dark surface, and what is drawn on it turns dark.
   A token a section starts using must be added here: tests/unit/components/SettingsDarkTheme.spec.js
   fails until it is. */
body.dark .fts-settings {
  --inistate-color-shade-white: var(--fts-gray-25);
  --inistate-color-neutral-50: var(--fts-gray-50);
  --inistate-color-neutral-100: var(--fts-gray-100);
  --inistate-color-neutral-200: var(--fts-gray-200);
  --inistate-color-neutral-300: var(--fts-gray-300);
  --inistate-color-neutral-400: var(--fts-gray-400);
  --inistate-color-neutral-600: var(--fts-gray-600);
  --inistate-color-neutral-700: var(--fts-gray-700);
  --inistate-color-neutral-800: var(--fts-gray-800);
  --inistate-color-neutral-900: var(--fts-gray-900);

  --inistate-color-primary-50: rgba(43, 158, 198, 0.14);
  --inistate-color-primary-100: rgba(43, 158, 198, 0.34);
  --inistate-color-primary-500: #2b9ec6;
  --inistate-color-primary-700: #4eb5d9;
  --inistate-color-primary-800: #a7daec;

  --inistate-color-green-50: rgba(164, 206, 58, 0.14);
  --inistate-color-green-800: #c2de7c;
  --inistate-color-green-900: #d2e69d;

  --inistate-color-orange-50: rgba(251, 140, 0, 0.14);
  --inistate-color-orange-100: rgba(251, 140, 0, 0.36);
  --inistate-color-orange-500: #fb8c00;
  --inistate-color-orange-900: #ffb352;

  --inistate-color-red-50: rgba(255, 77, 77, 0.14);
  --inistate-color-red-100: rgba(255, 77, 77, 0.38);
  --inistate-color-red-600: #ff8888;

  --fts-settings-on-accent: #0c1f27;
}

.fts-settings-nav {
  width: 272px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 20px 16px 16px;
  background: var(--fts-settings-ground);
  border-right: 1px solid var(--fts-settings-line);
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow-y: auto;
}

.fts-settings-nav-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.fts-settings-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);
  text-decoration: none;

  &:hover {
    color: var(--fts-settings-ink);
  }
}

.fts-settings-ws {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px 8px 8px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 10px;
  background: var(--inistate-color-shade-white);
}

.fts-settings-ws-mark {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--fts-settings-accent);
  color: var(--fts-settings-on-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.8125rem;
}

.fts-settings-ws-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.fts-settings-ws-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fts-settings-ws-sub {
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--fts-settings-mute);
}

.fts-settings-find {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  box-sizing: border-box;
  padding: 0 10px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 8px;
  background: var(--inistate-color-shade-white);
  color: var(--fts-settings-faint);

  input {
    border: none;
    outline: none;
    background: transparent;
    font: inherit;
    font-size: 0.8125rem;
    color: var(--fts-settings-ink);
    flex-grow: 1;
    min-width: 0;
    padding: 0;
  }
}

.fts-settings-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fts-settings-group-name {
  padding: 0 10px 6px;
  font-size: 0.6875rem;
  line-height: 1rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fts-settings-faint);
}

.fts-settings-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  box-sizing: border-box;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--fts-settings-ink-2);
  text-decoration: none;

  &:hover {
    background: var(--inistate-color-shade-white);
    color: var(--fts-settings-ink);
  }

  &.is-active {
    background: var(--inistate-color-shade-white);
    border-color: var(--fts-settings-line);
    color: var(--fts-settings-ink);
    font-weight: 500;
  }
}

.fts-settings-item-icon {
  width: 16px;
  color: var(--fts-settings-faint);

  .fts-settings-item.is-active & {
    color: var(--fts-settings-accent);
  }
}

.fts-settings-item-name {
  flex-grow: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fts-settings-item-note,
.fts-settings-item-out {
  font-size: 0.75rem;
  color: var(--fts-settings-faint);
  flex-shrink: 0;
}

.fts-settings-item-out {
  opacity: 0;

  .fts-settings-item:hover & {
    opacity: 1;
  }
}

.fts-settings-nav-empty {
  padding: 0 10px;
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);
}

.fts-settings-main {
  flex-grow: 1;
  min-width: 0;
  box-sizing: border-box;
  padding: 40px 64px 48px;
  overflow-y: auto;
}

@media (max-width: 1024px) {
  .fts-settings-main {
    padding: 24px 24px 32px;
  }
}

/* ---- shared section furniture ---------------------------------------- */

.fts-settings-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1120px;
}

.fts-settings-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;

  h1 {
    margin: 0;
    font-size: 1.625rem;
    line-height: 2.125rem;
    font-weight: 600;
    letter-spacing: -0.02em;
  }

  p {
    margin: 4px 0 0;
    font-size: 0.875rem;
    line-height: 1.375rem;
    color: var(--fts-settings-mute);
    max-width: 640px;
  }
}

.fts-settings-card {
  box-sizing: border-box;
  background: var(--inistate-color-shade-white);
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  h2 {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 600;
  }
}

.fts-settings-card-sub {
  margin: 2px 0 0;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-mute);
}

.fts-settings-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 1180px) {
  .fts-settings-grid-3 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.fts-settings-eyebrow {
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--fts-settings-faint);
}

.fts-settings-big {
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.fts-settings-btn {
  height: 36px;
  box-sizing: border-box;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--fts-settings-line);
  background: var(--inistate-color-shade-white);
  color: var(--fts-settings-ink);
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--fts-settings-ground);
  }

  &:disabled {
    background: var(--fts-settings-ground);
    color: var(--fts-settings-faint);
  }

  &.is-primary {
    background: var(--fts-settings-accent);
    border-color: var(--fts-settings-accent);
    color: var(--fts-settings-on-accent);

    &:hover:not(:disabled) {
      background: var(--inistate-color-primary-800);
    }

    /* the plain :disabled rule above ties on specificity, so a disabled primary
       button would otherwise still read as the page's one live action */
    &:disabled {
      background: var(--fts-settings-ground);
      border-color: var(--fts-settings-line);
      color: var(--fts-settings-faint);
    }
  }
}

.fts-settings-switch {
  width: 40px;
  height: 24px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 2px;
  border: none;
  border-radius: 999px;
  background: var(--inistate-color-neutral-400);
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span {
    width: 20px;
    height: 20px;
    border-radius: 999px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  }

  &[aria-checked="true"] {
    background: var(--fts-settings-accent);
    justify-content: flex-end;
  }

  &:disabled {
    opacity: 0.55;
  }
}

.fts-settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid var(--fts-settings-hair);

  &:last-child {
    border-bottom: none;
  }
}

.fts-settings-row-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  strong {
    font-weight: 500;
  }

  span {
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--fts-settings-mute);
  }
}

.fts-settings-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  background: var(--inistate-color-primary-50);
  border: 1px solid var(--inistate-color-primary-100);
  color: var(--inistate-color-primary-800);

  &.is-warn {
    background: var(--inistate-color-orange-50);
    border-color: var(--inistate-color-orange-100);
    color: var(--inistate-color-orange-900);
  }

  &.is-danger {
    background: var(--inistate-color-red-50);
    border-color: var(--inistate-color-red-100);
    color: var(--inistate-color-red-600);
  }

  strong {
    display: block;
    font-weight: 600;
  }
}

.fts-settings-note {
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--fts-settings-faint);

  &.is-error {
    color: var(--inistate-color-red-600);
  }
}

/* Billing contacts: one address per row, its Remove on the right. */
.fts-settings-contacts {
  margin: 0;
  padding: 0;
  list-style: none;
  max-width: 560px;

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 44px;
    border-top: 1px solid var(--fts-settings-hair);
    font-size: 0.875rem;

    &:first-child {
      border-top: none;
    }

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

/* Label-and-value rows, as the States board draws them (state 7). */
.fts-settings-facts {
  margin: 0;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fts-settings-fact {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.8125rem;
  line-height: 1.25rem;

  dt {
    color: var(--fts-settings-mute);
  }

  dd {
    margin: 0;
    font-weight: 500;
    text-align: right;
  }
}

.fts-settings-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fts-settings-textarea {
  box-sizing: border-box;
  width: 100%;
  min-height: 190px;
  padding: 12px 14px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 8px;
  background: var(--inistate-color-shade-white);
  color: var(--fts-settings-ink);
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.375rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--fts-settings-accent);
    box-shadow: 0 0 0 1px var(--fts-settings-accent);
  }

  &::placeholder {
    color: var(--inistate-color-neutral-400);
  }
}

.fts-settings-textarea-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: -8px;
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--fts-settings-faint);
}

.fts-settings-capacity {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}

@media (max-width: 1180px) {
  .fts-settings-capacity {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 20px;
  }
}

.fts-settings-capacity-cell {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding-left: 24px;
  border-left: 1px solid var(--fts-settings-hair);

  &:first-child {
    padding-left: 0;
    border-left: none;
  }
}

@media (max-width: 1180px) {
  .fts-settings-capacity-cell:nth-child(odd) {
    padding-left: 0;
    border-left: none;
  }
}

.fts-settings-capacity-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);

  svg {
    color: var(--fts-settings-faint);
  }
}

.fts-settings-capacity-value {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.fts-settings-capacity-used {
  font-size: 1.375rem;
  line-height: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.fts-settings-capacity-of {
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);
  white-space: nowrap;
}

.fts-settings-meter {
  height: 6px;
  border-radius: 999px;
  background: var(--fts-settings-hair);
  overflow: hidden;
  display: flex;
}

.fts-settings-meter-fill {
  background: var(--inistate-color-primary-500);
  border-radius: 999px;

  &.is-warn {
    background: var(--inistate-color-orange-500);
  }
}

/* An uncapped dimension gets a hairline where the meter would be, so the cells
   still line up without implying a bar that is empty or full. */
.fts-settings-meter-none {
  height: 6px;
  display: flex;
  align-items: center;

  &::after {
    content: "";
    flex-grow: 1;
    height: 1px;
    background: var(--fts-settings-line);
  }
}

.fts-settings-capacity-note {
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--fts-settings-faint);
  text-wrap: pretty;
}

/* ---- plan comparison ---- */

.fts-settings-compare {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.fts-settings-ruler {
  display: flex;
  gap: 18px;
}

.fts-settings-ruler-side {
  width: 190px;
  flex-shrink: 0;
  padding-top: 30px;
}

.fts-settings-ruler-track {
  flex-grow: 1;
  min-width: 0;
}

.fts-settings-ruler-heads {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  height: 30px;
  align-items: center;
}

.fts-settings-ruler-head {
  text-align: center;
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);

  &.is-current {
    font-weight: 600;
    color: var(--fts-settings-ink);
  }
}

.fts-settings-ruler-row {
  height: 62px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.fts-settings-ruler-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);

  svg {
    color: var(--fts-settings-faint);
  }
}

.fts-settings-ruler-used {
  font-size: 0.9375rem;
  font-weight: 600;
}

.fts-settings-ruler-bar {
  position: relative;
  height: 8px;
  border-radius: 999px;
  background: var(--fts-settings-hair);
}

.fts-settings-ruler-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 999px;
  background: var(--inistate-color-primary-500);
}

.fts-settings-ruler-marker {
  position: absolute;
  top: -4px;
  width: 16px;
  height: 16px;
  margin-left: -8px;
  box-sizing: border-box;
  border-radius: 999px;
  background: var(--inistate-color-shade-white);
  border: 4px solid var(--fts-settings-accent);
}

.fts-settings-ruler-ticks {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);

  span {
    text-align: right;
    padding-right: 6px;
    font-size: 0.6875rem;
    line-height: 1rem;
    color: var(--fts-settings-faint);
  }
}

.fts-settings-plans {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 1180px) {
  .fts-settings-plans {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.fts-settings-plan {
  box-sizing: border-box;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &.is-current {
    border-color: var(--fts-settings-accent);
    box-shadow: 0 0 0 1px var(--fts-settings-accent);
  }
}

.fts-settings-plan-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 24px;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }
}

.fts-settings-plan-tag {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--fts-settings-accent-soft);
  color: var(--inistate-color-primary-800);
  white-space: nowrap;
}

.fts-settings-plan-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.fts-settings-plan-amount {
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.fts-settings-plan-per {
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);
}

.fts-settings-plan-fit {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 36px;
  font-size: 0.75rem;
  line-height: 1.125rem;

  &.is-ok {
    color: var(--inistate-color-green-900);
  }

  &.is-warn {
    color: var(--inistate-color-orange-900);
  }

  svg {
    margin-top: 2px;
    flex-shrink: 0;
  }
}

/* SS06209: a first subscription is bought from the plan's card; pinned to the card's foot so
   the cards' actions line up whatever their feature lists hold. */
.fts-settings-plan-buy {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fts-settings-plan-feats {
  margin: 0;
  display: flex;
  flex-direction: column;

  > div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0;
    border-bottom: 1px solid var(--fts-settings-hair);

    &:last-child {
      border-bottom: none;
    }
  }

  dt {
    font-size: 0.8125rem;
    color: var(--fts-settings-mute);
  }

  dd {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 500;
    text-align: right;
  }
}

/* ---- cost estimate ---- */

.fts-settings-estimate {
  display: flex;
  gap: 32px;
  align-items: stretch;
}

@media (max-width: 1180px) {
  .fts-settings-estimate {
    flex-direction: column;
    gap: 20px;
  }
}

.fts-settings-estimate-controls {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.fts-settings-estimate-total {
  width: 340px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 20px;
  border-radius: 12px;
  background: var(--fts-settings-ground);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (max-width: 1180px) {
  .fts-settings-estimate-total {
    width: auto;
  }
}

.fts-settings-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fts-settings-field-label {
  font-size: 0.8125rem;
  font-weight: 500;
}

.fts-settings-seg {
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  border-radius: 9px;
  background: var(--fts-settings-ground);
  border: 1px solid var(--fts-settings-line);
  align-self: flex-start;
  flex-wrap: wrap;

  button {
    height: 32px;
    box-sizing: border-box;
    padding: 0 14px;
    border: 1px solid transparent;
    border-radius: 7px;
    background: transparent;
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--fts-settings-mute);
    display: inline-flex;
    align-items: center;
    gap: 8px;

    small {
      font-size: 0.6875rem;
      font-weight: 500;
      opacity: 0.8;
    }

    &.is-on {
      background: var(--inistate-color-shade-white);
      border-color: var(--fts-settings-line);
      color: var(--fts-settings-ink);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    }
  }
}

.fts-settings-stepper-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.fts-settings-stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--fts-settings-line);
  border-radius: 8px;
  overflow: hidden;
  background: var(--inistate-color-shade-white);

  button {
    width: 36px;
    height: 36px;
    border: none;
    background: var(--inistate-color-shade-white);
    color: var(--fts-settings-ink);
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      color: var(--inistate-color-neutral-300);
    }
  }

  output {
    min-width: 44px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1px solid var(--fts-settings-line);
    border-right: 1px solid var(--fts-settings-line);
    font-size: 0.9375rem;
    font-weight: 600;
  }
}

.fts-settings-stepper-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
  max-width: 300px;
  font-size: 0.8125rem;
  color: var(--fts-settings-mute);
}

.fts-settings-estimate-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  font-size: 0.875rem;

  span:first-child {
    display: flex;
    flex-direction: column;
  }

  small {
    font-size: 0.75rem;
    color: var(--fts-settings-faint);
  }

  &.is-total {
    font-size: 1rem;
    font-weight: 600;

    strong {
      font-weight: 600;
    }
  }
}

.fts-settings-estimate-rule {
  height: 1px;
  background: var(--fts-settings-line);
}

.fts-settings-estimate-delta {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-ink-2);
  min-height: 40px;
  text-wrap: pretty;
}

.fts-settings-enrolment {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--fts-settings-ground);
}

.fts-settings-enrolment-bar {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fts-settings-enrolment-text {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.8125rem;
  color: var(--fts-settings-ink-2);

  span:last-child {
    color: var(--fts-settings-mute);
  }
}

/* ---- AI credits ---- */

.fts-settings-credits {
  display: flex;
  gap: 32px;
  align-items: stretch;
}

@media (max-width: 1180px) {
  .fts-settings-credits {
    flex-direction: column;
    gap: 20px;
  }
}

.fts-settings-credits-balance {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (max-width: 1180px) {
  .fts-settings-credits-balance {
    width: auto;
  }
}

.fts-settings-credits-big {
  font-size: 3rem;
  line-height: 3.25rem;
  font-weight: 600;
  letter-spacing: -0.03em;
}

.fts-settings-credits-facts {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--fts-settings-ink-2);
}

.fts-settings-credits-chart {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-left: 1px solid var(--fts-settings-hair);
  padding-left: 32px;
}

@media (max-width: 1180px) {
  .fts-settings-credits-chart {
    border-left: none;
    padding-left: 0;
  }
}

.fts-settings-credits-chart-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  font-size: 0.8125rem;
  font-weight: 500;
}

.fts-settings-credits-legend {
  display: flex;
  gap: 14px;
  font-weight: 400;
  color: var(--fts-settings-mute);

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
  }

  i {
    width: 9px;
    height: 9px;
    border-radius: 3px;
  }
}

.fts-settings-credits-bars {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 150px;
}

.fts-settings-credits-day {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-start;
  height: 100%;
  border-radius: 3px 3px 0 0;
  overflow: hidden;

  i {
    display: block;
    width: 100%;
  }
}

.fts-settings-credits-axis {
  display: flex;
  justify-content: space-between;
  font-size: 0.6875rem;
  color: var(--fts-settings-faint);
}

/* The two halves of the sidebar -- you, and this workspace -- each headed by whom it is about,
   with a rule between them so they read as two places, not one list. */
.fts-settings-half {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.fts-settings-half + .fts-settings-half {
  padding-top: 18px;
  border-top: 1px solid var(--fts-settings-line);
}

/* You: a round photo where the workspace has its square mark. */
.fts-settings-ws.is-person {
  background: transparent;
  border-color: transparent;
  padding-left: 2px;
}

.fts-settings-person-mark {
  flex-shrink: 0;
}

.fts-settings-item.is-signout {
  color: var(--fts-settings-mute);
}

.fts-settings-signout-name {
  flex-grow: 1;
  min-width: 0;
}

.fts-settings-version {
  padding: 0 10px;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--fts-settings-faint);
}

.fts-settings-scope {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-mute);
}

/* Profile: the photo beside the fields, stacking on a phone. */
.fts-settings-profile {
  display: flex;
  gap: 28px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.fts-settings-profile-fields {
  flex: 1 1 360px;
}

.fts-settings-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  img {
    width: 112px;
    height: 112px;
    border-radius: 999px;
    object-fit: cover;
    border: 1px solid var(--fts-settings-line);
    background: var(--fts-settings-ground);
  }
}

.fts-settings-photo-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

/* A grid, not a flex column: fts-form-element reserves ~490px of height per field outside a
   grid cell, which spread the three fields over two screens. */
.fts-settings-password {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 4px;
  max-width: 420px;
}

.fts-settings-password-actions {
  padding-top: 8px;
}

/* A credential: monospaced so each character can be checked, and wrapping rather than
   overflowing the card on a phone. */
.fts-settings-key {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  code {
    flex: 1 1 260px;
    min-width: 0;
    padding: 8px 12px;
    border: 1px solid var(--fts-settings-line);
    border-radius: 8px;
    background: var(--fts-settings-ground);
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--fts-settings-ink);
    overflow-wrap: anywhere;
  }
}

/* General: logo, icon and the two brand colours, side by side and wrapping. */
.fts-settings-brand {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.fts-settings-brand-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 180px;
}

.fts-settings-colour {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fts-settings-colour-dot {
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 8px;
  background: var(--inistate-color-neutral-50);
  cursor: pointer;
}

.fts-settings-input {
  box-sizing: border-box;
  width: 110px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 8px;
  font: inherit;
  font-size: 0.875rem;
  color: var(--fts-settings-ink);

  &:focus {
    outline: none;
    border-color: var(--fts-settings-accent);
    box-shadow: 0 0 0 1px var(--fts-settings-accent);
  }
}

.fts-settings-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 20px;
}

@media (max-width: 760px) {
  .fts-settings-grid-2 {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* The save bar only exists while there is something to save, so it never sits there inert. */
.fts-settings-save {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;
  background: var(--inistate-color-neutral-50);
}

.fts-settings-save-actions {
  display: flex;
  gap: 8px;
}

.fts-settings-card.is-danger {
  border-color: var(--inistate-color-red-100);
}

.fts-settings-btn.is-danger {
  border-color: var(--inistate-color-red-100);
  color: var(--inistate-color-red-600);
}

.fts-settings-textarea.is-short {
  min-height: 96px;
}

/* Usage: one bar per measured day. Kept apart from the credits chart, which stacks four
   categories inside each day; these are a single value and read as a trend. */
.fts-settings-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 140px;
}

.fts-settings-bar {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column-reverse;
  height: 100%;

  i {
    display: block;
    width: 100%;
    border-radius: 3px 3px 0 0;
    background: var(--fts-settings-accent);
  }

  &.is-records i {
    background: var(--inistate-color-neutral-300);
  }
}

.fts-settings-axis {
  display: flex;
  justify-content: space-between;
  font-size: 0.6875rem;
  color: var(--fts-settings-faint);
}

/* The period picker and Export sit beside the page title, and wrap under it on a phone. */
.fts-settings-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.fts-settings-packs {
  display: flex;
  gap: 16px;
}

@media (max-width: 1180px) {
  .fts-settings-packs {
    flex-direction: column;
  }
}

.fts-settings-pack {
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
  padding: 16px 18px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;
  background: var(--inistate-color-shade-white);
  text-align: left;
  font: inherit;
  color: var(--fts-settings-ink);
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.is-on {
    border-color: var(--fts-settings-accent);
    box-shadow: 0 0 0 1px var(--fts-settings-accent);
  }
}

.fts-settings-pack-credits {
  font-size: 1.125rem;
  font-weight: 600;

  small {
    font-size: 0.8125rem;
    font-weight: 400;
    color: var(--fts-settings-mute);
  }
}

.fts-settings-pack-price {
  font-size: 1.375rem;
  line-height: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.fts-settings-pack-note {
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--fts-settings-mute);
}

.fts-settings-pack-buy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

/* One input and its button: a voucher code, a billing contact. */
.fts-settings-voucher,
.fts-settings-contact-add {
  display: flex;
  gap: 8px;
  max-width: 420px;

  input {
    flex-grow: 1;
    min-width: 0;
    height: 36px;
    box-sizing: border-box;
    padding: 0 12px;
    border: 1px solid var(--fts-settings-line);
    border-radius: 8px;
    background: var(--inistate-color-shade-white);
    font: inherit;
    font-size: 0.875rem;
    color: var(--fts-settings-ink);

    &:focus {
      outline: none;
      border-color: var(--fts-settings-accent);
      box-shadow: 0 0 0 1px var(--fts-settings-accent);
    }
  }
}

.fts-settings-table {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;

  th {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--fts-settings-faint);
    text-align: left;
    padding: 8px 12px 8px 0;
    border-bottom: 1px solid var(--fts-settings-line);
  }

  td {
    font-size: 0.8125rem;
    padding: 10px 12px 10px 0;
    border-bottom: 1px solid var(--fts-settings-hair);
  }

  tr:last-child td {
    border-bottom: none;
  }

  .is-right {
    text-align: right;
    padding-right: 0;
  }

  /* Right-aligned but NOT flush: a money column that still has a column after it. */
  .is-amount {
    text-align: right;
  }

  /* A date is one thing to read, and breaks over three lines when it is allowed to wrap. Only
     where there is room for it: holding it on one line at phone width pushed the last column
     (an invoice's PDF link) past the card's edge, where it could not be reached at all. */
  @media (min-width: 561px) {
    .is-date {
      white-space: nowrap;
    }
  }

  .is-muted {
    color: var(--fts-settings-mute);
  }

  .is-neg {
    color: var(--fts-settings-ink);
  }

  /* A second line under a cell: an invoice number below what it was for. */
  td small {
    display: block;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--fts-settings-faint);
  }
}

/* Billing: the next invoice's total beside the day it falls due. */
.fts-settings-next {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.fts-settings-next-when {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-mute);
}

/* A status word that has to read as a state rather than as prose -- an invoice's Paid or Due. */
.fts-settings-pill {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  line-height: 1.125rem;
  background: var(--fts-settings-hair);
  color: var(--fts-settings-mute);

  &.is-ok {
    background: var(--inistate-color-green-50);
    color: var(--inistate-color-green-800);
  }

  &.is-warn {
    background: var(--inistate-color-orange-50);
    color: var(--inistate-color-orange-900);
  }
}

.fts-settings-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--fts-settings-accent);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

/* An expiry that is about to bite, inside a row that is otherwise plain. */
.is-warn-text {
  color: var(--inistate-color-orange-900);
}

.fts-settings-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--fts-settings-mute);
  font-size: 0.8125rem;
}

/* ---- phone: the sidebar is the first screen (D-11, board 10) ----------------
   Last in the file on purpose: these override rules of equal specificity above. */

.fts-settings-bar-title,
.fts-settings-item-chev,
.fts-settings-phone-back {
  display: none;
}

@media (max-width: 767px) {
  .fts-settings {
    display: block;
    overflow-y: auto;
    background: var(--fts-settings-ground);
    font-size: 0.9375rem;
    line-height: 1.375rem;

    &.is-phone-page {
      background: var(--inistate-color-shade-white);
    }
  }

  .fts-settings-nav {
    width: auto;
    padding: 0 16px 32px;
    gap: 16px;
    border-right: none;
    background: transparent;
    overflow: visible;
  }

  /* The top bar: 56 px, pinned, with a 44 px way back to the workspace. */
  .fts-settings-nav-top {
    position: sticky;
    top: 0;
    z-index: 2;
    height: 56px;
    box-sizing: border-box;
    margin: 0 -16px;
    padding: 0 12px 0 6px;
    justify-content: flex-start;
    background: var(--inistate-color-shade-white);
    border-bottom: 1px solid var(--fts-settings-line);
  }

  .fts-settings-back {
    width: 44px;
    height: 44px;
    padding: 0;
    justify-content: center;
    font-size: 1.125rem;
    color: var(--fts-settings-ink);

    span {
      display: none;
    }
  }

  .fts-settings-bar-title {
    display: block;
    flex-grow: 1;
    font-size: 1.0625rem;
    line-height: 1.5rem;
    font-weight: 600;
  }

  .fts-settings-ws {
    min-height: 64px;
    padding: 12px 14px;
    border-radius: 14px;
  }

  .fts-settings-find {
    height: 44px;
    border-radius: 10px;

    /* 16 px keeps iOS from zooming the page when the field takes focus. */
    input {
      font-size: 1rem;
    }
  }

  /* Each group is one white card of 52 px rows. */
  .fts-settings-group {
    gap: 0;
  }

  /* The account group has no heading (the person above it names it), so its first row rounds
     the card's top itself. */
  .fts-settings-group > .fts-settings-item:first-child {
    border-radius: 14px 14px 0 0;
  }

  .fts-settings-group > .fts-settings-item:first-child:last-child {
    border-radius: 14px;
  }

  /* Log out stands alone: a card of one row. */
  .fts-settings-half > .fts-settings-item.is-signout {
    border: 1px solid var(--fts-settings-line);
    border-radius: 14px;
    color: var(--fts-settings-mute);
  }

  .fts-settings-ws.is-person {
    min-height: 0;
    padding: 4px 2px;
  }

  .fts-settings-group-name {
    padding: 0 4px 6px;
    font-size: 0.75rem;
  }

  .fts-settings-item {
    height: auto;
    min-height: 52px;
    gap: 14px;
    padding: 0 14px;
    border: 1px solid var(--fts-settings-line);
    border-bottom: none;
    border-radius: 0;
    background: var(--inistate-color-shade-white);
    color: var(--fts-settings-ink);
    font-size: 0.9375rem;

    &:hover,
    &.is-active {
      border-color: var(--fts-settings-line);
      background: var(--inistate-color-shade-white);
      font-weight: 400;
    }

    & + & {
      border-top-color: var(--fts-settings-hair);
    }

    &:last-child {
      border-bottom: 1px solid var(--fts-settings-line);
      border-radius: 0 0 14px 14px;
    }
  }

  .fts-settings-group-name + .fts-settings-item {
    border-radius: 14px 14px 0 0;

    &:last-child {
      border-radius: 14px;
    }
  }

  /* The frame colours every link (`.fts-settings a`), which outranks a bare class, so the
     rows and the bars restate their ink one level up. */
  .fts-settings .fts-settings-item,
  .fts-settings .fts-settings-back,
  .fts-settings .fts-settings-phone-back {
    color: var(--fts-settings-ink);
  }

  .fts-settings-item-icon {
    width: 19px;
    color: var(--fts-settings-mute);
  }

  /* No hover on a phone, so the "opens elsewhere" mark is always shown. */
  .fts-settings-item-out {
    opacity: 1;
  }

  .fts-settings-item-chev {
    display: inline-block;
    flex-shrink: 0;
    font-size: 0.8125rem;
    color: var(--fts-settings-faint);
  }

  /* A page on its own, with the way back pinned above it. */
  .fts-settings-main {
    padding: 0 16px 32px;
    overflow: visible;
  }

  .fts-settings-phone-back {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 4px;
    height: 56px;
    box-sizing: border-box;
    margin: 0 -16px 16px;
    padding: 0 16px 0 6px;
    background: var(--inistate-color-shade-white);
    border-bottom: 1px solid var(--fts-settings-line);
    color: var(--fts-settings-ink);
    font-size: 1.0625rem;
    line-height: 1.5rem;
    font-weight: 600;
    text-decoration: none;

    svg {
      width: 44px;
      font-size: 1.125rem;
    }
  }

  .fts-settings-page {
    gap: 16px;
  }

  .fts-settings-card {
    padding: 16px 14px;
    border-radius: 14px;
  }

  .fts-settings-capacity {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }

  .fts-settings-capacity-cell,
  .fts-settings-capacity-cell:nth-child(odd) {
    padding-left: 0;
    border-left: none;
  }
}
</style>
